<template>
  <div class="app-container">
    <!-- 登录页直接显示，不显示其他组件 -->
    <router-view v-if="isLoginPage"></router-view>
    
    <!-- 非登录页显示完整布局 -->
    <div v-else>
      <!-- 顶部栏 -->
      <HeaderComponent 
        :is-desktop="isDesktop" 
        @toggle-menu="toggleMenu"
      />
      
      <!-- 主体内容区 -->
      <div class="main-wrapper">
        <!-- 左侧菜单 -->
        <MenuComponent 
          :is-desktop="isDesktop" 
          :is-menu-open="isMenuOpen"
          @menu-click="handleMenuClick"
        />
        
        <!-- 右侧主内容 -->
        <main class="app-main" :class="{ 'main-collapsed': !isMenuOpen && !isDesktop }">
          <!-- 电脑端Tab标签 -->
          <div class="tab-container" v-if="isDesktop">
            <div class="tab-bar">
              <div 
                v-for="tab in tabs" 
                :key="tab.path"
                class="tab-item"
                :class="{ 'active': activeTab === tab.path }"
                @click="switchTab(tab)"
              >
                <span class="tab-title">{{ tab.name }}</span>
                <span 
                  class="tab-close"
                  @click.stop="closeTab(tab)"
                >×</span>
              </div>
            </div>
            <div class="tab-content">
              <router-view></router-view>
            </div>
          </div>
          <!-- 手机端直接显示内容 -->
          <div v-else>
            <router-view></router-view>
          </div>
          <!-- 底部信息 -->
          <FooterComponent />
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderComponent from './components/HeaderComponent.vue'
import MenuComponent from './components/MenuComponent.vue'
import FooterComponent from './components/FooterComponent.vue'

export default {
  name: 'App',
  components: {
    HeaderComponent,
    MenuComponent,
    FooterComponent
  },
  data() {
    return {
      isDesktop: true,
      isMenuOpen: true,
      // Tab相关数据
      tabs: [],
      activeTab: '',
      // 路由信息映射（扩展为包含所有主要路由）
      routeMap: {
        '/home': { name: '首页' },
        '/table': { name: '数据管理' },
        '/report': { name: '数据报表' },
        '/system/admin': { name: '管理员管理' },
        '/system/menu': { name: '菜单管理' },
        '/system/permission': { name: '权限管理' },
        '/system/role': { name: '角色管理' },
        '/system/tag': { name: '标签管理' },
        '/system/group': { name: '分组管理' },
        '/device/list': { name: '设备管理' },
        '/temple': { name: '寺庙管理' },
        '/temple/hall': { name: '场景管理' },
        '/user': { name: '用户列表' },
        '/place_user': { name: '场地用户列表' },
        '/order': { name: '订单管理' },
        '/work/otherBless': { name: '祈福管理' },
        '/mine': { name: '个人中心' },
        '/message': { name: '消息管理' },
        '/custom/list': { name: '客户管理' },
        '/qrcode': { name: '二维码管理' },
        '/dictionaries/hall': { name: '殿堂字典' },
        '/dictionaries/subscribe': { name: '订阅列表' },
        '/dictionaries/customer': { name: '客户标签' },
        '/calendar': { name: '日历管理' },
        '/library': { name: '素材库' },
        '/tool/list': { name: '工具管理' }
      }
    }
  },
  computed: {
    // 判断是否为登录页
    isLoginPage() {
      return this.$route.path === '/login'
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    // 初始化默认tab（非登录页）
    if (this.$route.path !== '/login') {
      this.addTab(this.$route)
    }
  },
  watch: {
    // 监听路由变化，添加新tab
    '$route': {
      handler(to, from) {
        // 登录页不添加tab
        if (to.path !== '/login') {
          this.addTab(to)
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      const width = window.innerWidth
      this.isDesktop = width > 768
      // 电脑端默认展开菜单，手机端默认收起菜单
      if (this.isDesktop) {
        this.isMenuOpen = true
      } else {
        this.isMenuOpen = false
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    // 添加tab
    addTab(route) {
      const path = route.path
      let name = '未命名页面'
      
      // 尝试从routeMap中获取名称
      if (this.routeMap[path] && this.routeMap[path].name) {
        name = this.routeMap[path].name
      } else {
        // 如果routeMap中没有定义，尝试从路由配置中获取名称
        const matchedRoute = this.$router.options.routes.find(r => r.path === path)
        if (matchedRoute && matchedRoute.name) {
          name = matchedRoute.name
        }
      }
      
      // 检查tab是否已存在
      const existingTab = this.tabs.find(tab => tab.path === path)
      if (!existingTab) {
        this.tabs.push({
          path: path,
          name: name
        })
      }
      // 设置当前激活的tab
      this.activeTab = path
    },
    // 切换tab
    switchTab(tab) {
      this.activeTab = tab.path
      this.$router.push(tab.path)
    },
    // 关闭tab
    closeTab(tab) {
      // 不允许关闭最后一个tab
      if (this.tabs.length <= 1) return
      
      const index = this.tabs.indexOf(tab)
      this.tabs.splice(index, 1)
      
      // 如果关闭的是当前激活的tab，切换到前一个或后一个tab
      if (this.activeTab === tab.path) {
        const newActiveIndex = index > 0 ? index - 1 : 0
        const newActiveTab = this.tabs[newActiveIndex]
        this.switchTab(newActiveTab)
      }
    },
    // 处理菜单点击事件，在手机端点击菜单后关闭菜单
    handleMenuClick() {
      this.isMenuOpen = false
    }
  }
}
</script>

<style scoped>
/* 使用全局样式中定义的 CSS 变量 */
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background);
}

/* 主体内容区布局 */
.main-wrapper {
  display: flex;
  flex: 1;
  margin-top: 80px; /* 为顶部栏留出空间 */
  height: calc(100vh - 80px); /* 减去顶部栏和底部栏的高度，使用固定高度而非min-height */
  overflow: hidden; /* 隐藏主内容区的滚动条 */
}

/* 主内容区样式 */
.app-main {
  flex: 1;
  padding: 0;
  background-color: var(--bg-200);
  transition: margin-left 0.3s ease;
}

/* Tab标签样式 */
.tab-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

/* Tab栏样式 */
.tab-bar {
  display: flex;
  background-color: var(--light);
  border-bottom: 1px solid var(--border);
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
}

/* Webkit滚动条样式 */
.tab-bar::-webkit-scrollbar {
  height: 4px;
}

.tab-bar::-webkit-scrollbar-track {
  background: transparent;
}

.tab-bar::-webkit-scrollbar-thumb {
  background-color: var(--border);
  border-radius: 2px;
}

.tab-bar::-webkit-scrollbar-thumb:hover {
  background-color: var(--border-light);
}

/* Tab项样式 */
.tab-item {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  cursor: pointer;
  border-right: 1px solid var(--border);
  transition: all 0.3s ease;
  position: relative;
}

.tab-item:hover {
  background-color: var(--border-light);
}

/* 激活的Tab项样式 */
.tab-item.active {
  background-color: var(--bg-200);
  color: var(--primary);
  border-bottom: 2px solid var(--primary);
}

/* Tab标题样式 */
.tab-title {
  margin-right: 8px;
  font-size: var(--font-size-sm);
  font-weight: 500;
}

/* Tab关闭按钮样式 */
.tab-close {
  font-size: var(--font-size-xs);
  color: var(--text-200);
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.tab-close:hover {
  opacity: 1;
  color: var(--danger);
}

/* Tab内容区样式 */
.tab-content {
  flex: 1;
  padding: 15px;
  padding-bottom:0;
  overflow-y: auto;
  background-color: var(--bg-200);
}

/* 电脑端样式 */
@media (min-width: 769px) {
  .app-main {
    margin-left: 0;
    overflow: hidden; /* 电脑端隐藏主内容区滚动条 */
    padding: 10px;
    box-sizing: border-box;
    background-color: var(--background);
    border-radius: 20px;
  }

  .tab-container{
    height: calc(100% - 42px);
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
  
  .main-collapsed {
    margin-left: 0;
  }
}

/* 手机端样式 */
@media (max-width: 768px) {
  .app-main {
    position: absolute;
    top: 80px;
    height: calc(100% - 80px);
    left: 0;
    right: 0;
    bottom: 0;
    transform: translateX(240px); /* 与手机端菜单宽度一致 */
    transition: transform 0.3s ease;
    padding: 15px;
    padding-top: 0;
    z-index: 998;
    overflow-y: auto; /* 手机端允许垂直滚动 */
  }
  
  .main-collapsed {
    transform: translateX(0);
  }
}
</style>